import type { QuestionnaireDraftStagedSave } from "./questionnaire_draft";
import { snakeToCamel } from "../lib";
import {
  IProject,
  IQuestionnaire,
  IQuestionnaireDisplayStrategy,
  IQuestionnaireQuestion,
  IQuestionnaireSection,
  ITask,
  ITaskSet,
  ITimer,
  ITodo,
  IWorkspace,
  MovesenseECGSampleRate,
  MovesenseSampleRate,
  NumberInputConfig,
  QuestionnaireQuestionType,
  ReminderItem,
  SelectInputConfig,
  StopwatchGarminStreamAccSampleRate,
  TaskType,
} from "../shared/db";
import {
  IGarminDevice,
  IStopwatchGarminStream,
  IStopwatchMovesenseStream,
  ITaskSchedule,
} from "../shared/db";
import {
  TempQuestionnaireDisplayStrategyUpsertItem,
  UpsertDisplayStrategy,
} from "./questionnaire_display_strategy";
import { ActionableTempTaskSchedule } from "./schedule";
import CronParser from "cron-parser";

export enum ThirdPartyTaskType {
  Dexcom = "dexcom",
  GarminConnect = "garmin_connect",
}

export type QuestionnaireContentListItem =
  | QuestionnaireTempSection
  | IQuestionnaireSection
  | QuestionnaireTempQuestion
  | IQuestionnaireQuestion;

export type TaskStateType = ITask;

export interface TempTask extends Omit<
  ITask,
  | "createdAt"
  | "updatedAt"
  | "todo"
  | "timer"
  | "stopwatchGarminStream"
  | "taskSet"
  | "questionnaire"
  | "garminDevice"
  | "taskSetParentId"
  | "taskSetParent"
  | "stopwatchMovesenseStream"
> {
  actionType: "create" | "update" | "hide" | "show";
  todo?: TempTodo;
  timer?: TempTimer;
  stopwatchGarminStream?: TempStopwatchGarminStream;
  taskSet?: TempTaskSet;
  questionnaire?: TempQuestionnaire;
  displayStrategyList?: TempQuestionnaireDisplayStrategyUpsertItem[];
  garminDevice?: TempGarminDevice;
  stopwatchMovesenseStream?: TempStopwatchMovesenseStream;
  liveEditingScheduleQueue?: ActionableTempTaskSchedule[];
  // the recovery copy a staged questionnaire came from, which its publish finishes
  draftSave?: QuestionnaireDraftStagedSave;
  // a staged new questionnaire's create key, which its publish sends
  createKey?: string;
}

export type TempTodo = Omit<ITodo, "id" | "createdAt" | "updatedAt">;
export type TempTimer = Omit<ITimer, "id" | "createdAt" | "updatedAt">;
export interface TempStopwatchGarminStream {
  name?: string;
  description?: string;
  accEnable?: boolean;
  accHz?: StopwatchGarminStreamAccSampleRate;
  timeLimitSeconds?: number;
}
export type TempTaskSet = Omit<ITaskSet, "id" | "createdAt" | "updatedAt">;
export type TempGarminDevice = Omit<IGarminDevice, "id" | "createdAt" | "updatedAt">;
export interface TempStopwatchMovesenseStream {
  taskId?: string;
  projectId?: string;
  workspaceId?: string;
  project?: IProject;
  workspace?: IWorkspace;
  name?: string;
  description?: string;
  task?: ITask;
  rrEnable?: boolean;
  imuEnable?: boolean;
  accEnable?: boolean;
  ecgEnable?: boolean;
  gyroscopeEnable?: boolean;
  magnetometerEnable?: boolean;
  ecgHz?: MovesenseECGSampleRate;
  imuHz?: MovesenseSampleRate;
  accHz?: MovesenseSampleRate;
  gyroscopeHz?: MovesenseSampleRate;
  magnetometerHz?: MovesenseSampleRate;
  timeLimitSeconds?: number;
}

export enum ContentListItemType {
  Section = "section",
  Question = "question",
}
export interface ContentListItem {
  id: string;
  type: ContentListItemType;
  item: QuestionnaireTempSection | QuestionnaireTempQuestion;
  // section?: QuestionnaireTempSection
  // question?: QuestionnaireTempQuestion
  // category: T
  // item: ItemType
}

export interface TempQuestionnaire extends Pick<
  IQuestionnaire,
  "taskId" | "projectId" | "workspaceId" | "name" | "description"
> {
  sectionList: QuestionnaireTempSection[];
  questionList?: QuestionnaireTempQuestion[];
}

export interface QuestionnaireTempSection extends Pick<
  IQuestionnaireSection,
  "id" | "description" | "legend" | "mediaUrl"
> {
  index?: number;
  questionnaireId?: string;
  questionList?: QuestionnaireTempQuestion[];
  displayStrategyId?: string;
  displayStrategy?: UpsertDisplayStrategy | IQuestionnaireDisplayStrategy | null;
}
export interface QuestionnaireTempQuestion<
  T extends QuestionnaireQuestionType = QuestionnaireQuestionType,
  TempInput = T extends
    | QuestionnaireQuestionType.Select
    | QuestionnaireQuestionType.MultiSelect
    | QuestionnaireQuestionType.Radio
    ? QuestionnaireTempInputConfigOptions
    : undefined,
> extends Pick<
  IQuestionnaireQuestion,
  "id" | "description" | "required" | "label" | "type" | "mediaUrl"
> {
  index?: number;
  questionnaireId?: string;
  questionnaireSectionId?: string;
  inputConfig?: TempInput | null;
  displayStrategyId?: string;
  displayStrategy?: UpsertDisplayStrategy | IQuestionnaireDisplayStrategy | null;
}
export interface QuestionnaireTempInputConfigOptions {
  customOtherName?: string;
  allowOther?: boolean;
  showSlider?: boolean;
  integer?: boolean;
  max?: number;
  min?: number;
  maxLabel?: string;
  minLabel?: string;
  options?: {
    id?: string;
    value: string;
    name: string;
  }[];
}

export type TaskContentName =
  | "todo"
  | "timer"
  | "questionnaire"
  | "stopwatchMovesenseStream"
  | "stopwatchGarminStream";

export const getTaskContentName = (taskType: TaskType | ThirdPartyTaskType): TaskContentName => {
  if (
    taskType == TaskType.StopwatchMovesenseStream ||
    taskType == TaskType.StopwatchMovesenseLogData
  ) {
    return "stopwatchMovesenseStream";
  }
  return snakeToCamel(taskType) as TaskContentName;
};

const formatCronString = (cronParseObj: CronParser.CronExpression<false>) => {
  const { dayOfWeek, hour, minute } = cronParseObj.fields;

  const timeString = `${hour[0].toString().padStart(2, "0")}:${minute[0].toString().padStart(2, "0")}`;
  const AMPM = hour[0] < 12 ? "AM" : "PM";

  // (0 or 7 is Sun) from 'cron-parser'
  const daysOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  const formattedDaysOfWeek = dayOfWeek.map((index: number) => daysOfWeek[index]);

  let formattedString = "Repeats";

  if (dayOfWeek.length >= 7) {
    formattedString += ` daily at ${timeString} ${AMPM}`;
  } else if (dayOfWeek.length === 1) {
    formattedString += ` every ${formattedDaysOfWeek[0]} at ${timeString} ${AMPM}`;
  } else {
    const lastDayOfWeek = formattedDaysOfWeek.pop();
    formattedString += ` every ${formattedDaysOfWeek.join(", ")} and ${lastDayOfWeek} at ${timeString} ${AMPM}`;
  }
  return formattedString;
};

const formatISOString = (dateString: string) => {
  const utcDate = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };
  const timezoneOffset = new Date().getTimezoneOffset() * 60 * 1000;
  return new Date(utcDate.getTime() + timezoneOffset).toLocaleString(undefined, options);
};

export const generateReminderString = (reminder: ReminderItem) => {
  if (reminder.type === "cron") {
    const cronInterval = CronParser.parseExpression(reminder.value);
    return formatCronString(cronInterval);
  } else if (reminder.type === "date") {
    return formatISOString(reminder.value);
  }
};

export type EditingTask = ITask | TempTask;

export type GarminDeviceSpecCopy = Omit<
  IGarminDevice,
  | "accelerometerEnable"
  | "batteryPercentageSupport"
  | "id"
  | "workspaceId"
  | "projectId"
  | "taskId"
  | "createdAt"
  | "updatedAt"
>;

export interface tempStopwatchMovesenseStreamSpec {
  task?: ITask;
  project?: IProject;
  workspace?: IWorkspace;
  rrEnable?: boolean;
  imuEnable?: boolean;
  accEnable?: boolean;
  ecgEnable?: boolean;
  gyroscopeEnable?: boolean;
  magnetometerEnable?: boolean;
  ecgHz?: MovesenseECGSampleRate;
  imuHz?: MovesenseSampleRate;
  accHz?: MovesenseSampleRate;
  gyroscopeHz?: MovesenseSampleRate;
  magnetometerHz?: MovesenseSampleRate;
  timeLimitSeconds?: number;
}

export interface tempStopwatchGarminStreamSpec {
  accEnable?: boolean;
  accHz?: StopwatchGarminStreamAccSampleRate;
  timeLimitSeconds?: number;
}

export type TaskTypeForDevicePage =
  | "garmin_device"
  | "smartphone"
  | "dexcomIntegrationForm"
  | "dexcomSelectorPopup"
  | "polar"
  | "";
export type TaskTypeForInstructionPage =
  | TaskType.StopwatchMovesenseStream
  | TaskType.StopwatchGarminStream
  | "";
